/**
 * Gestion du chargement différé des scripts non critiques
 * Améliore le FCP en retardant les scripts lourds
 */

/**
 * Charger un script de manière asynchrone
 */
export function loadScriptAsync(src: string, attributes: Record<string, string> = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;

    // Ajouter les attributs personnalisés
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.body.appendChild(script);
  });
}

/**
 * Charger Google Tag Manager de manière différée
 * Attend que la page soit complètement chargée + 1 seconde
 */
export function loadGTMDeferred(gtmId: string): void {
  if (typeof window === 'undefined') return;

  const loadGTM = () => {
    // Vérifier si GTM n'est pas déjà chargé
    if (window.dataLayer) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);
  };

  // Charger après le load complet + 1 seconde
  if (document.readyState === 'complete') {
    setTimeout(loadGTM, 1000);
  } else {
    window.addEventListener('load', () => {
      setTimeout(loadGTM, 1000);
    });
  }
}

/**
 * Charger le widget Readdy de manière différée
 * Attend que la page soit interactive
 */
export function loadReaddyWidgetDeferred(projectId: string): void {
  if (typeof window === 'undefined') return;

  const loadWidget = () => {
    // Vérifier si le widget n'est pas déjà chargé
    if (document.querySelector('script[src*="readdy.ai/api/public/assistant/widget"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = `https://readdy.ai/api/public/assistant/widget?projectId=${projectId}`;
    script.setAttribute('mode', 'hybrid');
    script.setAttribute('voice-show-transcript', 'true');
    script.setAttribute('theme', 'light');
    script.setAttribute('size', 'compact');
    script.setAttribute('accent-color', '#14B8A6');
    script.setAttribute('button-base-color', '#0D9488');
    script.setAttribute('button-accent-color', '#FFFFFF');
    script.setAttribute('main-label', 'Expert IA');
    script.setAttribute('start-button-text', 'Démarrer');
    script.setAttribute('end-button-text', 'Terminer');
    script.defer = true;

    document.body.appendChild(script);
  };

  // Charger dès que la page est interactive
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(loadWidget, 500);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(loadWidget, 500);
    });
  }
}

/**
 * Initialiser tous les scripts différés
 */
export function initDeferredScripts(): void {
  // GTM - Charger après 1 seconde
  loadGTMDeferred('GTM-XXXXXXX');

  // Readdy Widget - Charger après 500ms
  loadReaddyWidgetDeferred('3e7025d6-106c-4a53-ac84-206527161ea9');
}

/**
 * Différer tous les scripts non critiques
 * Appelé après le premier rendu pour ne pas bloquer le FCP
 */
export function deferNonCriticalScripts(): void {
  initDeferredScripts();
}

/**
 * Déclarer le type pour window.dataLayer
 */
declare global {
  interface Window {
    dataLayer: any[];
  }
}